import React from "react";

import { orderBy, uniq } from "lodash-es";

import {
  FlowVersion,
  FlowVersionFlowChild,
  FlowVersionStatus,
} from "src/api/flowTypes";
import { DependencyItem } from "src/base-components/DependencyList";
import { AgentVersionsDependingOnFlowResponseItem } from "src/clients/agent-management";
import { FlowDbShallow } from "src/clients/flow-api";
import { Modal } from "src/design-system/Modal";
import { Skeleton } from "src/design-system/Skeleton";
import { VersionStatusPill } from "src/flow/Pills";
import {
  getBaseUrl,
  getUrlToAgentVersionPage,
  getUrlToAuthoringPage,
} from "src/router/urls";

const getSortedPublishedVersions = (versions: FlowVersion[]) => {
  const releasedVersions = versions.filter(
    (version) => version.status === FlowVersionStatus.PUBLISHED,
  );
  return orderBy(releasedVersions, ["meta.published_at"], ["desc"]);
};
const getSortedDraftVersions = (versions: FlowVersion[]) => {
  const draftVersions = versions.filter(
    (version) => version.status === FlowVersionStatus.DRAFT,
  );
  return orderBy(draftVersions, ["created_at"], ["desc"]);
};
const getSortedArchivedVersions = (versions: FlowVersion[]) => {
  const archivedVersions = versions.filter(
    (version) => version.status === FlowVersionStatus.ARCHIVED,
  );
  return orderBy(archivedVersions, ["meta.archived_at"], ["desc"]);
};

export const isLastPublishedVersion = (
  versions: FlowVersion[],
  version: FlowVersion,
) => {
  const sortedVersions = getSortedPublishedVersions(versions);
  return version.id === sortedVersions.at(-1)?.id;
};

export const isLastDraftVersion = (
  versions: FlowVersion[],
  version: FlowVersion,
) => {
  const sortedVersions = getSortedDraftVersions(versions);
  return version.id === sortedVersions.at(-1)?.id;
};

export const getSortedVersions = (versions: FlowVersion[]) => {
  const sortedDrafts = getSortedDraftVersions(versions);
  const sortedReleased = getSortedPublishedVersions(versions);
  const sortedArchived = getSortedArchivedVersions(versions);

  return [...sortedReleased, ...sortedDrafts, ...sortedArchived];
};

export const getLatestReleaseVersion = (versions: FlowVersion[]) => {
  const sortedVersions = getSortedPublishedVersions(versions);
  if (sortedVersions.length > 0) {
    return sortedVersions[0];
  }
};

export const getNameErrorMessage = (type: string): string => {
  if (type === "required") {
    return "Decision Flow version name cannot be empty";
  } else if (type === "notAvailable") {
    return "Decision Flow version name already in use";
  } else if (type === "noLeadingTrailingSpaces") {
    return "Decision Flow version name cannot have leading or trailing spaces";
  } else if (type === "disallowedCharacters") {
    return "Please only use letters, digits, dashes, underscores, spaces and dots.";
  } else {
    return "";
  }
};

// Allowed characters: letters, digits, dashes, underscores, dots, spaces, brackets, colons, square brackets.
const VERSION_NAME_ALLOWED_CHARS = /^[A-Za-z0-9\-_.\s[\]()]*$/;

export const nameValidations = {
  noLeadingTrailingSpaces: (name: string) => name === name.trim(),
  disallowedCharacters: (name: string) => VERSION_NAME_ALLOWED_CHARS.test(name),
};

export type GroupedRelatedFlows = Record<
  string,
  { name: string; versions: FlowDbShallow["versions"] }
>;

export const groupRelatedFlows = (
  flows: FlowDbShallow[],
): [string[], GroupedRelatedFlows] => {
  return [
    uniq(flows.map((flow) => flow.id)),
    flows.reduce((acc, flow) => {
      if (acc[flow.id]) {
        acc[flow.id].versions = [
          ...(acc[flow.id].versions ?? []),
          ...(flow.versions ?? []),
        ];
      } else {
        acc[flow.id] = {
          name: flow.name,
          versions: flow.versions ?? [],
        };
      }
      return acc;
    }, {} as GroupedRelatedFlows),
  ];
};

export const agentVersionsToDependencyItems = (
  agentVersions: AgentVersionsDependingOnFlowResponseItem[],
  orgId: string,
  wsId: string,
  statusPill: React.ReactNode,
): DependencyItem[] =>
  agentVersions.map(({ id, agent_name, name, agent_id }) => ({
    id,
    name: `${agent_name} - ${name}`,
    group: "Agents",
    href: getBaseUrl() + getUrlToAgentVersionPage(orgId, wsId, agent_id, id),
    statusPill,
  }));

export const flowParentsToDependencyItems = (opts: {
  flowVersion: FlowVersion | undefined;
  orgId: string;
  wsId: string;
  statusFilter?: "published" | "draft";
  filterArchivedVersions?: boolean;
}): DependencyItem[] => {
  const {
    flowVersion,
    orgId,
    wsId,
    statusFilter,
    filterArchivedVersions = false,
  } = opts;

  const statusMap: Record<string, FlowVersionStatus> = {
    published: FlowVersionStatus.PUBLISHED,
    draft: FlowVersionStatus.DRAFT,
  };

  const matchesFilter = (status: FlowVersionStatus): boolean =>
    (!statusFilter || status === statusMap[statusFilter]) &&
    (!filterArchivedVersions || status !== FlowVersionStatus.ARCHIVED);

  return (
    (flowVersion as FlowVersionFlowChild | undefined)?.parent_flows?.flatMap(
      (flow) =>
        (flow.versions ?? [])
          .filter((v) => matchesFilter(v.status))
          .map((v) => ({
            id: v.id,
            name: `${flow.name} - ${v.name}`,
            group: "Flows",
            href:
              getBaseUrl() + getUrlToAuthoringPage(orgId, wsId, flow.id, v.id),
            statusPill: <VersionStatusPill status={v.status} />,
          })),
    ) ?? []
  );
};

export const DependencyModalSkeleton: React.FC = () => (
  <>
    <Modal.Content>
      <div className="flex flex-col gap-y-4">
        {/* Description placeholder */}
        <div className="flex flex-col gap-y-1.5">
          <Skeleton height="h-3.5" width="w-full" />
          <Skeleton height="h-3.5" width="w-3/4" />
        </div>
        {/* Dependency item placeholder */}
        <Skeleton height="h-4" width="w-48" />
      </div>
    </Modal.Content>
    {/* Mimic Modal.Footer layout with skeleton button on the right */}
    <div className="flex justify-end px-5 py-4">
      <Skeleton height="h-8" rounded="rounded-lg" width="w-24" />
    </div>
  </>
);
